/**
 * Personalize tool, product page side.
 *
 * Opens the tool (assets/c365-tool.js) for the selected design variant + size and keeps track of the
 * customer's design for each template — the Personalize theme id, one per design variant × size
 * (localStorage `c365:designs:2`):
 *
 *   <c365-product> fires `c365:personalize` {variantId, themeId, properties}
 *     → tool.open(...): the tool page of this product in a full-screen frame
 *   left with unsaved changes → state "draft"
 *   Save Design               → state "ready", saving: the tool is hidden at once and uploads in the
 *                               background (10 s and more); the page shows the picture taken from the canvas
 *                             → state "ready" once the uploads are through
 *                               (or the tool comes back with its "Try Again")
 *
 * The page hears about it through `c365:design` {state: none | draft | ready, key, image, at, properties,
 * saving, picture} (key = the theme id; saving = uploads still running, no properties yet; picture = the
 * canvas picture to show instead of downloading `image`)
 * and shows the "Your design is in progress / is ready" card (Continue personalizing / Add to cart, Edit
 * design, or start a new design). `properties` (Preview, _design_id, _preview_image, _print_file) go on the
 * cart line on top of what the 365Canvas Options app prices from. `c365:preview` {image} carries a ready
 * design's picture for Room Art, and `c365:designs` {states: {theme id: draft | ready}} every design of this
 * product, for the "Draft" / "Saved" badges on the size tiles. The page answers with:
 *   `c365:design-added`    the ready design went into the cart → forgotten, the page is new again
 *   `c365:design-discard`  "start a new design" confirmed      → forgotten, and the tool's working copy too
 *
 * The tool keeps its own working copy in localStorage per template, so opening it again for the same size
 * brings back what the customer entered. It lasts 7 days by the tool's own rule; a design recorded here
 * stretches that to the 30 days designs are kept (keepWork). It is cleared only by "start a new design", by
 * the tool's own Reset, or by time. (Editing a design that is already in the cart is the cart's business:
 * assets/c365-cart.js.)
 */
import { THEME_ROOT, clearWork, designProperties, hasWork, keepWork, markWork, readDesigns, tool, toolProduct, writeDesign } from '@c365/tool';

class Personalize {
  constructor(pdp) {
    this.pdp = pdp;
    this.designs = readDesigns();
    this.pending = new Set();
    this.saving = null; // {key, picture, at}: a save whose uploads are still running
    this.pictures = {}; // theme id → canvas picture of a design saved during this page view
    this.shown = null;
    this.states = null;
    pdp.addEventListener('c365:personalize', (e) => {
      e.preventDefault();
      this.open(e.detail);
    });
    // a design belongs to one template; it shows only while that design variant + size is selected
    pdp.addEventListener('c365:change', () => this.sync());
    pdp.addEventListener('c365:design-added', (e) => this.save(e.detail.key, null));
    pdp.addEventListener('c365:design-discard', (e) => {
      clearWork(e.detail.key);
      this.save(e.detail.key, null);
    });
    this.restore();
  }

  /** Template of the current selection: the Personalize theme id ('' for a static design). */
  get key() {
    return this.pdp.themeId || '';
  }

  open({ variantId, themeId, properties, productId }) {
    const pdp = this.pdp;
    const [w, h] = pdp.size.split('x');
    tool.open({
      page: window.location.pathname,
      themeId,
      globalTool: {
        // the tool knows products by their Woo id (templates and a few per-product rules are keyed by it)
        productId: Number(pdp.data.wooProductId) || Number(productId),
        themeRoot: THEME_ROOT,
      },
      productData: toolProduct({
        variantId,
        themeId,
        sizeCode: properties._size_code,
        sizeLabel: `${w}″ × ${h}″`,
        price: (pdp.optionPrice || 0) / 100,
        moneyFormat: pdp.dataset.moneyFormat,
        productUrl: window.location.origin + window.location.pathname,
      }),
      on: {
        saving: (pictures) => this.saveStart(themeId, pictures),
        done: (result) => this.finish(themeId, result),
        fail: () => this.saveFailed(themeId),
        draft: (changedAt) => this.draft(themeId, changedAt),
      },
    });
  }

  /**
   * Save Design, step 1: the tool has its picture and starts uploading. The customer does not wait for that:
   * the page shows the design as ready, with the picture from the canvas. Should the tab close before the
   * uploads are through, what is left is a design in progress.
   */
  saveStart(key, { image }) {
    this.saving = { key, picture: image, at: Date.now() };
    if (!this.designs[key]?.id) {
      this.designs[key] = { at: this.saving.at };
      writeDesign(key, this.designs[key]);
    }
    keepWork(key);
    this.sync();
  }

  /** The uploads did not go through: the tool is back on screen with its "Try Again". */
  saveFailed(key) {
    if (this.saving?.key === key) this.saving = null;
    this.sync();
  }

  /** Save Design, done: the design is on the tool's server and can go into the cart. */
  finish(key, { previewUrl, designId }) {
    if (this.saving?.key === key) {
      this.pictures[key] = this.saving.picture;
      this.saving = null;
    }
    keepWork(key);
    markWork(key, designId); // what the tool holds for this template is this design, until it is changed
    this.save(key, { id: designId, image: previewUrl, at: Date.now() });
  }

  /** The customer left the tool with unsaved changes. A design already saved stands until it is saved again. */
  draft(key, changedAt) {
    if (this.designs[key]?.id) return;
    keepWork(key);
    this.save(key, { at: changedAt });
  }

  save(key, design) {
    if (design) this.designs[key] = design;
    else delete this.designs[key];
    writeDesign(key, design);
    this.sync();
  }

  /**
   * Designs kept from an earlier visit. One in progress is dropped when the tool no longer has the work
   * behind it. A ready one is shown once its preview image has loaded; if it does not load it stays hidden
   * for this visit and is left in storage (it may only be the network).
   */
  restore() {
    const mine = new Set(this.pdp.data.variants.flatMap((v) => Object.values(v.themeIds || {})));
    Object.entries(this.designs).forEach(([key, design]) => {
      if (!mine.has(key)) return;
      if (!design.id) {
        if (!hasWork(key)) this.save(key, null);
        return;
      }
      this.pending.add(design);
      const img = new Image();
      img.onload = () => {
        this.pending.delete(design);
        this.sync();
      };
      img.onerror = () => {
        if (this.designs[key] === design) delete this.designs[key];
      };
      img.src = design.image;
    });
    this.sync();
  }

  /** Tells the page which design, if any, belongs to the current selection — and which templates have one. */
  sync() {
    const key = this.key;
    const saving = key && this.saving?.key === key ? this.saving : null;
    const visible = Object.entries(this.designs).filter(([, d]) => !this.pending.has(d));
    const states = Object.fromEntries(visible.map(([k, d]) => [k, d.id ? 'ready' : 'draft']));
    if (this.saving) states[this.saving.key] = 'ready';
    if (JSON.stringify(states) !== this.states) {
      this.states = JSON.stringify(states);
      document.dispatchEvent(new CustomEvent('c365:designs', { detail: { states } }));
    }

    const saved = key ? this.designs[key] : null;
    const design = saved && !this.pending.has(saved) ? saved : null;
    const shown = saving ? `saving|${saving.at}` : design ? `${design.id || ''}|${design.at}|${design.image}` : null;
    if (shown === this.shown) return;
    this.shown = shown;
    const ready = !!design?.id;
    let detail = { state: 'none', key };
    if (saving) {
      detail = { state: 'ready', saving: true, key, image: saving.picture, at: saving.at, properties: null };
    } else if (design) {
      detail = {
        state: ready ? 'ready' : 'draft',
        key,
        image: design.image || '',
        // saved during this page view: the picture taken from the canvas, so nothing has to be downloaded
        picture: (ready && this.pictures[key]) || null,
        at: design.at,
        properties: ready ? designProperties(design) : null,
      };
    }
    const picture = detail.state === 'ready' ? detail.picture || detail.image : null;
    document.dispatchEvent(new CustomEvent('c365:design', { detail }));
    document.dispatchEvent(new CustomEvent('c365:preview', { detail: { image: picture } }));
  }
}

customElements.whenDefined('c365-product').then(() => {
  document.querySelectorAll('c365-product').forEach((pdp) => new Personalize(pdp));
});
